// Share access by QR — a whole location, one room, or one device.
//
//   the sharer:  createShareCode()  -> a code the server signed, valid 5 minutes
//   the scanner: previewShare()     -> what it would give (shown before any write)
//                redeemShare()      -> the server writes location -> room -> device
//
// Backend: Home Automation api/new/shareAccess.js. Full account in
// docs/21-share-access.md.
//
// ⚠️ THE SERVER IS THE CLOCK AND THE JUDGE. The code is signed and timed on the
// server and checked there on redeem, so an edited code, an old screenshot or a
// typed unique number gets nowhere through the app. The old Flutter share QR
// (`u%#<uid>n%#<uno>`) had neither and worked forever; it is no longer accepted
// here (core/devices/claimDevice).
//
// ⚠️ The endpoints ship with the SERVER. Until that deploy they answer with an
// HTML 404, which every call here reports as "needs a server update" rather
// than as a bad code — the two must not look alike.

import { post } from '../api/api';
import { resyncSelf } from '../db/markSinkData';

export const SHARE_VALID_SECONDS = 300;

const SHARE_CODE_RE = /SLS1\.[LRD]\.\d+\.\d+\.[0-9a-z]+\.[A-Za-z0-9_-]{16}/;

/** The share code inside whatever was scanned or pasted, or null. */
export function findShareCode(raw) {
  const m = String(raw || '').replace(/\s+/g, '').match(SHARE_CODE_RE);
  return m ? m[0] : null;
}

/** True for the pre-2026-10 Flutter share QR, which carries no expiry. */
export const isLegacyShareCode = (raw) => /u%#\d+n%#/.test(String(raw || ''));

const NOT_DEPLOYED = {
  ok: false,
  reason: 'not-deployed',
  message: 'Sharing by QR needs a server update that has not been installed yet.',
};
const OFFLINE = {
  ok: false,
  reason: 'network',
  message: "Couldn't reach the server. Check your internet connection and try again.",
};

// ok:false replies come back as 4xx; axios throws on those, so the body is
// read off the error. A 404 with no JSON body is the route being absent.
async function call(endpoint, body) {
  try {
    const res = await post(endpoint, body);
    const d = res && res.data;
    return d && typeof d === 'object' && 'ok' in d ? d : NOT_DEPLOYED;
  } catch (e) {
    const r = e && e.response;
    if (r && r.data && typeof r.data === 'object' && 'ok' in r.data) return r.data;
    if (r && r.status === 404) return NOT_DEPLOYED;
    return OFFLINE;
  }
}

/**
 * target: { scope:'location', locationId } | { scope:'room', roomId }
 *       | { scope:'device', uniqueNumber, deviceInstalledId? }
 * -> { ok, code, expiresAt, validSeconds, scope, byName, location, room, device, rooms, devices }
 *  | { ok:false, reason, message }
 */
export function createShareCode(userId, target) {
  return call('CreateShareCode', {
    UserId: Number(userId),
    Scope: target.scope,
    LocationID: target.locationId != null ? Number(target.locationId) : undefined,
    RoomID: target.roomId != null ? Number(target.roomId) : undefined,
    UniqueNumber: target.uniqueNumber != null ? String(target.uniqueNumber) : undefined,
    DeviceInstalledID: target.deviceInstalledId != null ? Number(target.deviceInstalledId) : undefined,
  });
}

/** -> { ok, scope, byName, location, room, device, rooms, devices, already, expiresAt } */
export function previewShare(userId, code) {
  return call('PreviewShareCode', { UserId: Number(userId), Code: code });
}

/**
 * -> { ok, scope, …, added: { location, rooms, devices } }
 * On success the phone's cache is rebuilt BEFORE this returns — the next screen
 * reads rooms local-first, so the new rows must already be there.
 */
export async function redeemShare(userId, code) {
  const r = await call('RedeemShareCode', { UserId: Number(userId), Code: code });
  if (r.ok) await resyncSelf(userId);
  return r;
}

/** "Home — 3 rooms, 14 devices" style line for a preview or a result. */
export function describeShare(s) {
  if (!s) return '';
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
  if (s.scope === 'location') {
    return `${s.location?.name || 'Location'} — ${plural(s.rooms, 'room')}, ${plural(s.devices, 'device')}`;
  }
  if (s.scope === 'room') {
    return `${s.room?.name || 'Room'}${s.location ? ` in ${s.location.name}` : ''} — ${plural(s.devices, 'device')}`;
  }
  const where = [s.room?.name, s.location?.name].filter(Boolean).join(', ');
  return `${s.device?.name || 'Device'}${where ? ` (${where})` : ''}`;
}
